/**
 * What a setting is, and how its value survives a round trip.
 *
 * The vocabulary the settings panel is read with, with nothing in it about
 * which settings there are. One tool has a panel — the pinyin converter, whose
 * fields are `optionValues` in `options.ts` and whose copy is
 * `articles/data/pinyin_settings.yaml`, and this file is what keeps the
 * markup, the storage and the address bar from having to know that. The script
 * converter had a panel too until it started writing every script at once and
 * had nothing left to ask.
 *
 * Everything a value can arrive from is untrusted. A radio's `value` attribute
 * is a string even where the setting is a boolean, a URL was typed by hand, and
 * `localStorage` may have been written by a version of the tool that offered
 * choices this one does not. All three are answered the same way, by asking
 * which values the field actually offers.
 *
 * Structural rather than imported: `optionFields` in `options.ts` is what gets
 * passed in, and nothing here refers to it. That is the same separation the
 * `Control` interface this replaces was drawn for, minus the labels, which
 * moved to the page along with the markup that used to be built from them.
 */

/** One setting: its name, and every value it may take. */
export interface OfferedValues {
  readonly field: string;
  readonly values: readonly (string | boolean)[];
}

/** How a value is written into a radio's `value` attribute. */
export function valueAttribute(value: string | boolean): string {
  return typeof value === "boolean" ? String(value) : value;
}

/**
 * What a set of settings currently says about one field.
 *
 * The panel is a list of fields and a set of settings that the list is about,
 * and the two are only ever connected by the field name. This is the join, and
 * it is a function rather than an index because the settings are written as
 * interfaces with named fields: those describe the tools better than a bag of
 * strings would, and the cost of that is one narrowing here instead of an index
 * signature on every one of them.
 *
 * A field the settings do not hold, or hold something odd under, reads as
 * undefined. That is the same answer an unrecognised value gets everywhere
 * else in this file, and for the same reason.
 */
export function settingValue(
  settings: object,
  field: string,
): string | boolean | undefined {
  const value = (settings as Record<string, unknown>)[field];

  return typeof value === "string" || typeof value === "boolean"
    ? value
    : undefined;
}

/**
 * Read a setting's chosen value back, given what the radio carried.
 *
 * The attribute is always a string, so the booleans have to be recovered from
 * what the field offers rather than guessed at from the text.
 */
export function valueFromAttribute(
  offered: OfferedValues,
  attribute: string,
): string | boolean | undefined {
  return offered.values.find((value) => valueAttribute(value) === attribute);
}

/**
 * Read a set of settings off something untrusted.
 *
 * Every field is checked against the values it offers and anything that fails
 * falls back to the default, because the input is whatever was left in
 * `localStorage`, possibly by an older version of this tool.
 */
export function readSettings<Settings extends object>(
  fields: readonly OfferedValues[],
  defaults: Settings,
  value: unknown,
): Settings {
  if (typeof value !== "object" || value === null) {
    return defaults;
  }

  let read = defaults;

  for (const offered of fields) {
    const stored = settingValue(value, offered.field);

    if (stored !== undefined && offered.values.includes(stored)) {
      read = { ...read, [offered.field]: stored };
    }
  }

  return read;
}
